import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const API_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/** Calendar date for API / HTML date inputs (YYYY-MM-DD), without UTC shift. */
export function toApiDateString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const text = String(value).trim();
  const match = API_DATE_PATTERN.exec(text);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return toApiDateString(parsed);
  }
  return text;
}

export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function startOfDay(value: unknown): Date | null {
  const date = toFormDateValue(value);
  return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
}

export const OVERPAYMENT_EXTEND_ONLY_ERROR = 'overpaymentExtendOnly';

export function extendOnlyEndDateValidator(floor: Date | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const min = floor ? new Date(floor.getFullYear(), floor.getMonth(), floor.getDate()) : null;
    const value = startOfDay(control.value);
    if (!min || !value) {
      return null;
    }
    return value.getTime() < min.getTime() ? { [OVERPAYMENT_EXTEND_ONLY_ERROR]: true } : null;
  };
}

export function minSelectableStartDate(existing?: unknown): Date {
  const today = startOfToday();
  const saved = toFormDateValue(existing ?? null);
  if (saved) {
    const savedMidnight = new Date(saved.getFullYear(), saved.getMonth(), saved.getDate());
    if (savedMidnight.getTime() < today.getTime()) {
      return savedMidnight;
    }
  }
  return today;
}

/** Local Date for Material datepicker controls. */
export function toFormDateValue(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value == null || value === '') {
    return null;
  }
  return parseApiDateString(String(value));
}

/** Local Date from API date string. */
export function parseApiDateString(value: string | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }
  const match = API_DATE_PATTERN.exec(String(value).trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Value for `<input type="date">` from API response. */
export function toDateInputValue(value: string | null | undefined): string {
  return toApiDateString(value) ?? '';
}

export function formatApiDateString(
  value: string | undefined,
  separator: '/' | '.' = '/'
): string {
  if (!value) {
    return '—';
  }
  const match = API_DATE_PATTERN.exec(String(value).trim());
  if (match) {
    const [, y, m, d] = match;
    return `${d}${separator}${m}${separator}${y}`;
  }
  const parsed = parseApiDateString(value);
  if (!parsed) {
    return value;
  }
  const d = String(parsed.getDate()).padStart(2, '0');
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${d}${separator}${m}${separator}${parsed.getFullYear()}`;
}
